import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { CaretDown, Cloud, CloudSun, Sun, Crown, Info } from "phosphor-react";
import { format } from "date-fns";
import { UserContext } from "context/user";
import { MapActions, MapContext } from "context/map";
import { getCatalog } from "api/Farm";
import { useZonesQuery } from "hooks/useZones";
import useOnClickOutside from "hooks/useOnClickOutside";
import Button from "components/Button";
import "./index.scss";

const layers = [
  {
    name: "Standard Resolution",
    value: "standard",
  },
  {
    name: "Rapid-EM™",
    value: "sensor_em",
  },
];
const standardAlgorithms = [
  {
    title: "NDVI",
    description: "Normalized Difference Vegetation Index",
    enabled: false,
  },
  {
    title: "NDRE",
    description: "Normalized Difference Red Edge",
    enabled: false,
    advanced: "",
  },
  {
    title: "THERMAL",
    description: "Thermal",
    enabled: false,
    advanced: "",
  },
  {
    title: "PCD",
    description: "Plant Cell Density",
    enabled: false,
    advanced: "",
  },
];
const sensorEmAlgorithms = [
  {
    title: "SHALLOW",
    description: "For shallow soil management",
    enabled: true,
  },
  {
    title: "DEEP",
    description: "For deep soil management",
    enabled: true,
  },
];
const zoneFillColors = {
  2: ["match", ["get", "zone"], 1, "#ffe75e", 2, "#34ffa1", "#FFFFFF"],
  3: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#b9ff55",
    3,
    "#1260ff",
    "#FFFFFF",
  ],
  4: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#fcff54",
    3,
    "#54f174",
    4,
    "#1260ff",
    "#FFFFFF",
  ],
  5: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#ffe75e",
    3,
    "#b9ff55",
    4,
    "#34ffa1",
    5,
    "#1260ff",
    "#FFFFFF",
  ],
};

const Zones = ({
  numberOfZones,
  setNumberOfZones,
  resolutionLevel,
  setResolutionLevel,
  setActiveTab,
  setZoneLayerLoading,
  setZoneLayerBackgroundLoading,
}) => {
  const { token, profile } = useContext(UserContext);
  const catalogRef = useRef();
  const layerRef = useRef();
  const calculationRef = useRef();
  const { state, dispatch } = useContext(MapContext);
  const {
    selectedLayer,
    selectedAlgorithm,
    catalog,
    selectedDate,
    selectedFields,
    map,
    zoneLayerFetching,
    algorithms,
  } = state;
  const [algorithmList, setAlgorithmList] = useState(standardAlgorithms);
  const captureDateQuery = `&latest_capture_date=${selectedDate}`;
  const zonesQuery = `&zones=${numberOfZones}`;
  const resolutionQuery = `&resolution=${resolutionLevel}`;
  const algorithmQuery = `&algorithm=${selectedLayer}_${selectedAlgorithm.toLocaleLowerCase()}`;
  const query = `${captureDateQuery}${zonesQuery}${resolutionQuery}${algorithmQuery}`;
  const { data, error, isLoading, refetchAll, isStale } = useZonesQuery(
    query,
    selectedFields
  );
  const [isCatalogListOpen, setCatalogListOpen] = useState(false);
  const [isLayerListOpen, setIsLayerListOpen] = useState(false);
  const [isCalculationListOpen, setIsCalculationListOpen] = useState(false);
  const prevErrorRef = useRef(null);

  useOnClickOutside(catalogRef, () => setCatalogListOpen(false));
  useOnClickOutside(layerRef, () => setIsLayerListOpen(false));
  useOnClickOutside(calculationRef, () => setIsCalculationListOpen(false));

  useEffect(() => {
    selectedFields.forEach((fieldId) => {
      const fieldFill = map.getLayer(`fill-${fieldId}`);
      if (fieldFill && selectedDate) {
        map.removeLayer(`fill-${fieldId}`);
      }
    });
  }, [map, selectedDate, selectedFields]);

  useEffect(() => {
    if (selectedLayer === "sensor_em") {
      setAlgorithmList(sensorEmAlgorithms);

      return;
    }
    setAlgorithmList(standardAlgorithms);
  }, [selectedLayer]);

  useEffect(() => {
    if (selectedLayer) {
      if (selectedLayer === "sensor_em") return;
      const enabledAlgorithms = ["NDVI", "NDVI-R", "COLOUR"];
      const premiumAlgorithms = ["NDRE", "THERMAL", "PCD"];
      premiumAlgorithms.map((premiumAlgorithm) => {
        const enabled = profile.feature_flags.find(
          (item) =>
            item.feature_flag ===
              `algorithm:${premiumAlgorithm.toLowerCase()}` && item.enabled
        );

        if (enabled) {
          enabledAlgorithms.push(premiumAlgorithm);
        }

        return null;
      });
      setAlgorithmList((previous) =>
        previous.map((algorithm) => ({
          ...algorithm,
          enabled:
            selectedLayer === "standard"
              ? enabledAlgorithms.includes(algorithm.title)
              : algorithms.includes(algorithm.title),
          advanced:
            selectedLayer === "standard" &&
            !enabledAlgorithms.includes(algorithm.title) ? (
              <Crown color="#09ba51" size={20} />
            ) : (
              ""
            ),
        }))
      );
    }
  }, [selectedLayer, algorithms, profile]);

  const fetchCatalog = useCallback(async () => {
    const fieldsQuery = selectedFields
      .map((field) => `field_ids=${field}`)
      .join("&");
    const layerQuery = `&layer_filter=${selectedLayer}`;
    const searchDaysQuery =
      selectedLayer === "high_res" ? "" : "&search_days=365";
    const catalogQuery = `${fieldsQuery}${layerQuery}${searchDaysQuery}`;
    const catalogData = await getCatalog(catalogQuery, token);

    dispatch({ type: MapActions.SET_CATALOG, value: catalogData });
  }, [dispatch, selectedFields, selectedLayer, token]);

  useEffect(() => {
    if (catalog.length) return;

    fetchCatalog();
  }, [catalog.length, fetchCatalog]);

  useEffect(() => {
    let backgroundLoading;

    if (zoneLayerFetching) {
      setZoneLayerLoading(true);

      if (isStale && numberOfZones && resolutionLevel) {
        refetchAll();
      }

      dispatch({
        type: MapActions.SET_ZONE_LAYER_FETCHING,
        value: false,
      });
    }

    if (isLoading) {
      backgroundLoading = setTimeout(() => {
        setZoneLayerLoading(false);
        setZoneLayerBackgroundLoading(true);
      }, 20000);
    }

    return () => {
      clearTimeout(backgroundLoading);
    };
  }, [
    dispatch,
    isLoading,
    isStale,
    numberOfZones,
    refetchAll,
    resolutionLevel,
    setZoneLayerBackgroundLoading,
    setZoneLayerLoading,
    zoneLayerFetching,
  ]);

  const changeZoneLayerColor = () => {
    const fillColors = zoneFillColors[numberOfZones];

    if (fillColors) {
      map.setPaintProperty("zones", "fill-color", fillColors);
    }
  };

  useEffect(() => {
    if (data) {
      changeZoneLayerColor();
      const source = map.getSource("zones");
      source.setData(data);
      setZoneLayerLoading(false);
      setZoneLayerBackgroundLoading(false);
    }
  });

  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      setZoneLayerLoading(false);
      setZoneLayerBackgroundLoading(false);
      setNumberOfZones(null);
      setResolutionLevel(null);

      toast.error("Zone layer failed to load. Please try again.", {
        toastId: "zone-error",
      });
    }

    prevErrorRef.current = error;
  }, [
    error,
    setNumberOfZones,
    setResolutionLevel,
    setZoneLayerBackgroundLoading,
    setZoneLayerLoading,
  ]);

  const handleSelectLayer = (event) => {
    dispatch({
      type: MapActions.SET_SELECTED_LAYER,
      value: event,
    });
    setIsLayerListOpen(false);
  };

  const handleSelectCalculation = (event) => {
    dispatch({
      type: MapActions.SET_SELECTED_ALGORITHM,
      value: event,
    });
    setIsCalculationListOpen(false);
  };

  const handleSelectCatalog = (value) => {
    dispatch({
      type: MapActions.SET_SELECTED_DATE,
      value,
    });
    dispatch({
      type: MapActions.SET_TOGGLE_CALCULATIONS,
      value: false,
    });
    setCatalogListOpen(false);
  };

  const gotoRatesTab = () => {
    if (!data) return;
    dispatch({
      type: MapActions.SET_ZONE_DATA,
      value: data,
    });
    setActiveTab("rates");
  };

  const renderCloudIcon = (cloudPercent) => {
    if (cloudPercent === 0) {
      return <Sun size={20} color="#83858E" />;
    }

    if (cloudPercent <= 50) {
      return <CloudSun size={20} color="#83858E" />;
    }

    return <Cloud size={20} color="#83858E" />;
  };

  const renderCloudPercentage = (selected) => {
    const selectedCatalog =
      selected ||
      catalog.find((item) => {
        const date = item?.date || item.start_date;
        return date === selectedDate;
      });
    const selectedItem = selectedCatalog?.items?.find(
      (item) =>
        item.source.toLowerCase() === selectedLayer &&
        item.algorithm.toLowerCase() === selectedAlgorithm.toLowerCase()
    );
    return (
      <>
        {selectedItem?.cloud_percent || 0}%
        {renderCloudIcon(selectedItem?.cloud_percent || 0)}
      </>
    );
  };

  const getSelectedLayer = (item) => {
    let selectedItem = "";
    if (item === "standard") {
      selectedItem = "Standard Resolution";
    }
    if (item === "sensor_em") {
      selectedItem = "Rapid-EM™";
    }
    if (item === "high_res") {
      selectedItem = "High Resolution";
    }
    return selectedItem;
  };

  const renderLayerSelection = () => {
    return (
      <div className="menu-container -layer">
        <div className="zone-select" ref={layerRef}>
          <div
            className={`${
              isLayerListOpen ? "layer-selected -open" : "layer-selected"
            }`}
            aria-hidden
            onClick={() => setIsLayerListOpen(!isLayerListOpen)}
          >
            {selectedLayer ? (
              <p className="selected">
                {getSelectedLayer(selectedLayer)}
                <CaretDown weight="bold" size={20} color="#83858E" />
              </p>
            ) : null}
          </div>
          {isLayerListOpen && (
            <div className="layer-select-list">
              {layers.length > 0 ? (
                <>
                  {layers.map((layer) => (
                    <div
                      className="layer-item"
                      aria-hidden
                      onClick={() => handleSelectLayer(layer.value)}
                      key={layer.value}
                    >
                      {layer.name}
                    </div>
                  ))}
                </>
              ) : null}
            </div>
          )}
        </div>
        <div className="label">Layer</div>
      </div>
    );
  };

  const renderCalculationSelection = () => {
    return (
      <div className="menu-container -calculation">
        <div className="zone-select" ref={calculationRef}>
          <div
            className={`${
              isCalculationListOpen ? "layer-selected -open" : "layer-selected"
            }`}
            aria-hidden
            onClick={() => setIsCalculationListOpen(!isCalculationListOpen)}
          >
            {selectedAlgorithm ? (
              <p className="selected">
                {selectedAlgorithm}
                <CaretDown weight="bold" size={20} color="#83858E" />
              </p>
            ) : null}
          </div>
          {isCalculationListOpen && (
            <div className="layer-select-list">
              {algorithmList.length > 0 ? (
                <>
                  {algorithmList.map((algorithm) => (
                    <div
                      className="layer-item"
                      aria-hidden
                      onClick={() => handleSelectCalculation(algorithm.title)}
                      key={algorithm.title}
                      disabled={algorithm.enabled}
                      style={!algorithm.enabled ? { display: "none" } : {}}
                    >
                      {algorithm.title}
                    </div>
                  ))}
                </>
              ) : null}
            </div>
          )}
        </div>
        <div className="label">
          {selectedLayer === "sensor_em" ? "Depth" : "Algorithm"}
        </div>
      </div>
    );
  };

  const renderDateCatalog = () => {
    return (
      <div className="menu-container -date-catalog">
        <div className="zone-catalog" ref={catalogRef}>
          <div
            className={`${
              isCatalogListOpen ? "catalog-selected -open" : "catalog-selected"
            }`}
            aria-hidden
            onClick={() => setCatalogListOpen(!isCatalogListOpen)}
          >
            {selectedDate ? (
              <p className="selected">
                {format(new Date(selectedDate), "MMMM d, yyyy")}
                <span className="cloud">{renderCloudPercentage()}</span>
                <CaretDown weight="bold" size={20} color="#83858E" />
              </p>
            ) : (
              <p className="selected">
                Select date
                <CaretDown weight="bold" size={20} color="#83858E" />
              </p>
            )}
          </div>
          {isCatalogListOpen && (
            <div className="catalog-list">
              {catalog.length > 0 ? (
                <>
                  {catalog.map((item) => (
                    <div
                      className="catalog-item"
                      aria-hidden
                      onClick={() =>
                        handleSelectCatalog(item?.date || item.start_date)
                      }
                      key={item?.date || item.start_date}
                    >
                      {format(
                        new Date(item?.date || item.start_date),
                        "MMMM d, yyyy"
                      )}
                      <span className="cloud">
                        {renderCloudPercentage(item)}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="catalog-item -empty">No dates available</div>
              )}
            </div>
          )}
        </div>
        <div className="label">Date</div>
      </div>
    );
  };

  const renderNumberOfZones = () => {
    const zones = [2, 3, 4, 5];

    return (
      <div className="menu-container -number-of-zones">
        <div className="chip-container">
          {zones.map((zone) => (
            <div
              className={`${
                numberOfZones === zone ? "zoning-chip -active" : "zoning-chip"
              }`}
              key={zone}
              onClick={() => setNumberOfZones(zone)}
              aria-hidden
            >
              {zone}
            </div>
          ))}
        </div>
        <div className="label">Number of zones</div>
      </div>
    );
  };

  const renderResolutionLevel = () => {
    const resolutionLevels = [
      { level: 1, title: "5m" },
      { level: 2, title: "25m" },
      { level: 3, title: "50m" },
      { level: 4, title: "100m" },
    ];

    return (
      <div className="menu-container -resolution-levels">
        <div className="legends">
          <p className="text">Fine</p>
          <div />
          <div />
          <p className="text">Coarse</p>
        </div>
        <div className="resolution-container">
          {resolutionLevels.map((item) => (
            <div
              className={`${
                resolutionLevel === item.level
                  ? "reso-level -active"
                  : "reso-level"
              }`}
              key={item.level}
              onClick={() => setResolutionLevel(item.level)}
              aria-hidden
            >
              {item.title}
            </div>
          ))}
        </div>
        <div className="smooth-level">
          <ReactTooltip
            className="tooltip"
            place="right"
            type="dark"
            effect="float"
            multiline
          />
          <div className="label">Resolution Level</div>
          <div>
            <Info
              size={24}
              color="#8a8e99"
              weight="fill"
              data-tip="This presents the scale that you wish
              to apply the product at - from fine to coarse.
              For eg. a boom section control (5m) or a spreader (25m or 50m)."
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="zone-settings">
        {renderLayerSelection()}
        {selectedLayer && renderCalculationSelection()}
        {selectedAlgorithm && renderDateCatalog()}
        {selectedDate && renderNumberOfZones()}
        {numberOfZones && renderResolutionLevel()}
      </div>
      <div className="zoning-next">
        <Button
          color="black"
          block
          label="Next"
          disabled={!data}
          onClick={() => gotoRatesTab()}
        />
      </div>
    </>
  );
};

Zones.propTypes = {
  numberOfZones: PropTypes.number,
  setNumberOfZones: PropTypes.func.isRequired,
  resolutionLevel: PropTypes.number,
  setResolutionLevel: PropTypes.func.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  setZoneLayerLoading: PropTypes.func.isRequired,
  setZoneLayerBackgroundLoading: PropTypes.func.isRequired,
};

Zones.defaultProps = {
  numberOfZones: 2,
  resolutionLevel: 1,
};

export default Zones;
